import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { AnimatedNumber } from "@/components/SubscriberCounter";
import useTelegramSubscribers from "@/hooks/useTelegramSubscribers";
import { STATS } from "@/config";

function CountUp({ value, suffix }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.6 });
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, value, {
            duration: 2.2,
            ease: [0.22, 1, 0.36, 1],
            onUpdate: (v) => setN(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, value]);
    return (
        <span ref={ref}>
            {n.toLocaleString("it-IT")}
            {suffix}
        </span>
    );
}

function cellBorder(i, total) {
    if (total === 3) return i > 0 ? "border-t border-ink/5 sm:border-t-0 sm:border-l" : "";
    if (i === 0) return "";
    if (i === 1) return "border-t border-ink/5 sm:border-t-0 sm:border-l";
    if (i === 2) return "border-t border-ink/5 lg:border-t-0 lg:border-l";
    return "border-t border-ink/5 sm:border-l lg:border-t-0";
}

function SubscribersCard({ count }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.6 });
    return (
        <div
            data-testid="stat-subscribers"
            className="relative flex flex-col items-center justify-center px-6 py-10 text-center sm:py-14"
        >
            <Reveal>
                <span className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-600 ring-1 ring-emerald-200">
                    <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Live
                </span>
                <span
                    ref={ref}
                    className="block font-mono text-4xl font-extrabold tracking-tight text-ink sm:text-5xl"
                >
                    <AnimatedNumber value={count} trigger={inView} />
                </span>
                <span className="mt-3 block font-display text-[13px] font-medium uppercase tracking-[0.16em] text-slate-500">
                    Iscritti al canale
                </span>
            </Reveal>
        </div>
    );
}

export default function Stats() {
    const { status: subStatus, count: subCount } = useTelegramSubscribers();
    const hasReal = subStatus === "ok";
    const total = hasReal ? 4 : 3;
    const gridCls = hasReal ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-3";

    return (
        <section id="numeri" data-testid="stats" className="bg-paper py-16 sm:py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div
                    className={`grid ${gridCls} overflow-hidden rounded-3xl border border-ink/5 bg-white shadow-[0_12px_45px_-18px_rgba(11,18,32,0.1)]`}
                >
                    {hasReal && <SubscribersCard count={subCount} />}
                    {STATS.map((s, i) => {
                        const idx = hasReal ? i + 1 : i;
                        return (
                            <div
                                key={s.id}
                                data-testid={`stat-value-${i}`}
                                className={`flex flex-col items-center justify-center px-6 py-10 text-center sm:py-14 ${cellBorder(idx, total)}`}
                            >
                                <Reveal>
                                    <span className="block font-mono text-4xl font-extrabold tracking-tight text-ink sm:text-5xl">
                                        {s.value === null ? (
                                            <span>{s.display}</span>
                                        ) : (
                                            <CountUp value={s.value} suffix={s.suffix} />
                                        )}
                                    </span>
                                    <span className="mt-3 block font-display text-[13px] font-medium uppercase tracking-[0.16em] text-slate-500">
                                        {s.label}
                                    </span>
                                </Reveal>
                            </div>
                        );
                    })}
                </div>
                <Reveal delay={0.2}>
                    <p
                        data-testid="stats-demo-note"
                        className="mt-4 text-right font-mono text-[10px] uppercase tracking-[0.18em] text-slate-300"
                    >
                        {hasReal
                            ? "Iscritti: dato reale dal canale Telegram · * Altri valori dimostrativi"
                            : "* Valori dimostrativi, facilmente aggiornabili"}
                    </p>
                </Reveal>
            </div>
        </section>
    );
}
